<template>
  <StaffLayout section="Email Templates" showHeader>
    <div class="mt-4">
      <Loading v-if="isLoading" />
      <div v-else-if="emailTemplates" class>
        <div v-if="emailTemplates && emailTemplates.length > 0">
          <AddButton @click="addClick">Add Template</AddButton>
          <div
            v-for="template in emailTemplates"
            :key="template.settingId"
            class="px-4 py-2 pt-3 my-2 bg-gray-100 border-t cursor-pointer hover:bg-teal-200"
            @click="editClick(template)"
          >
            {{ template.settingName }}
          </div>
        </div>
        <div v-else class="mb-2">
          <span>-- No Templates found for this building --</span>
        </div>
        <div>
          <AddButton @click="addClick">Add Template</AddButton>
        </div>

        <EditPopup
          entity-name="Template"
          :is-visible="isPopupVisible"
          :is-edit-mode="isEditing"
          :save-error="saveError"
          :delete-enabled="true"
          :save-on-enter="false"
          :close-on-outside-click="false"
          @close="isPopupVisible = false"
          @save="saveClick"
          @delete="deleteClick"
        >
          <BaseInput
            type="text"
            v-model="templateName"
            label="Name"
            placeholder="Template Name"
            class="mb-2"
            :error="errors.templateName"
          />
          <div class="mb-8">
            <label class="block text-sm font-medium text-gray-700 mb-2">Text</label>
            <div v-if="Ckeditor && Editor" class="ckeditor-text">
              <component :is="Ckeditor" :editor="Editor" v-model="templateText" />
            </div>

            <!-- Template Variables Reference -->
            <div class="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
              <div class="text-sm font-medium text-gray-700 mb-2">
                Available Template Variables:
              </div>
              <div class="text-xs text-gray-600 space-y-1">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                  <div>
                    <span class="font-mono">Unique Person Number:</span>
                    <code class="bg-gray-200 px-1 rounded">[[personid]]</code>
                  </div>
                  <div>
                    <span class="font-mono">First Name:</span>
                    <code class="bg-gray-200 px-1 rounded">[[firstname]]</code>
                  </div>
                  <div>
                    <span class="font-mono">Last Name:</span>
                    <code class="bg-gray-200 px-1 rounded">[[lastname]]</code>
                  </div>
                  <div>
                    <span class="font-mono">Login Email:</span>
                    <code class="bg-gray-200 px-1 rounded">[[email]]</code>
                  </div>
                  <div>
                    <span class="font-mono">PortalId:</span>
                    <code class="bg-gray-200 px-1 rounded">[[portalid]]</code>
                  </div>
                  <div>
                    <span class="font-mono">Property Name:</span>
                    <code class="bg-gray-200 px-1 rounded">[[propertyname]]</code>
                  </div>
                  <div>
                    <span class="font-mono">Site Code:</span>
                    <code class="bg-gray-200 px-1 rounded">[[sitecode]]</code>
                  </div>
                  <div>
                    <span class="font-mono">Client Name:</span>
                    <code class="bg-gray-200 px-1 rounded">[[clientname]]</code>
                  </div>
                  <div>
                    <span class="font-mono">Tenant Name:</span>
                    <code class="bg-gray-200 px-1 rounded">[[tenantname]]</code>
                  </div>
                  <div>
                    <span class="font-mono">Date Most Recent Training:</span>
                    <code class="bg-gray-200 px-1 rounded">[[mostrecenttraining]]</code>
                  </div>
                  <div>
                    <span class="font-mono">Expire After # Months:</span>
                    <code class="bg-gray-200 px-1 rounded">[[expireaftermonths]]</code>
                  </div>
                  <div>
                    <span class="font-mono">Months Since Last Training:</span>
                    <code class="bg-gray-200 px-1 rounded"
                      >[[monthssincelasttraining]]</code
                    >
                  </div>
                </div>
              </div>
            </div>

            <div v-if="errors.templateText" class="text-red-500 text-sm mt-1">
              {{ errors.templateText }}
            </div>
          </div>
        </EditPopup>
      </div>
    </div>
  </StaffLayout>
</template>

<script setup lang="ts">
import { useAdminStore } from "../../store/adminStore";
import {
  SaveEmailTemplate,
  Setting,
  SettingType,
} from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useCKEditor } from "../../composables/useCKEditor";

const adminStore = useAdminStore();

// Load CKEditor using composable
const { Ckeditor, Editor } = useCKEditor();

var isLoading = ref(false);
var emailTemplates = ref([] as Setting[]);

onMounted(async () => {
  await loadData();
});

async function loadData() {
  isLoading.value = true;

  var response = await adminStore.getEmailTemplates();

  if (
    Array.isArray(response) &&
    response.length > 0 &&
    typeof response[0] === "object" &&
    "settingId" in response[0]
  ) {
    emailTemplates.value = response as Setting[];
  } else {
    console.error("Failed to load email templates:", response);
  }
  isLoading.value = false;
}

// Popup
let isEditing = ref(false);
let isPopupVisible = ref(false);
let saveError = ref("");

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    templateName: yup.string().required().max(50).label("Name"),
    templateText: yup.string().required().label("Text"),
  }),
  initialValues: {
    templateName: "",
    templateText: "",
  },
});

let settingId = ref(-1);
const { value: templateName } = useField<string>("templateName");
const { value: templateText } = useField<string>("templateText");

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  console.log("Valid form submission, saving template...");
  let saveTemplate = {
    settingId: settingId.value,
    settingName: templateName.value,
    settingValue: templateText.value,
    settingType: SettingType.ExpiredTrainingEmail,
  } as SaveEmailTemplate;

  saveError.value = await adminStore.saveEmailTemplate(saveTemplate);
  if (!saveError.value) {
    await loadData();
    isPopupVisible.value = false;
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

let addClick = function () {
  settingId.value = 0;
  templateName.value = "";
  templateText.value = "";

  saveError.value = "";
  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selected: Setting) {
  settingId.value = selected.settingId ?? 0;
  templateName.value = selected.settingName ?? "";
  templateText.value = selected.settingValue ?? "";

  saveError.value = "";
  isEditing.value = true;
  isPopupVisible.value = true;
};

async function deleteClick() {
  saveError.value = await adminStore.deleteEmailTemplate(settingId.value);
  if (!saveError.value) {
    await loadData();
    isPopupVisible.value = false;
  }
}
</script>
